import { formatNumber } from 'humanize-plus'
import moment from 'moment'
import { map } from 'ramda'

import { Report } from '@driftt/api-types'

import { LineGraphResponse } from '../types'

import { ParserFunction } from './types'

const ensureNoNan = (value): number => (value === 'NaN' ? 0 : value)

/**
 * Calculate the deflection rate using playbook-interaction-summary metrics.
 *
 * Row Definitions:
 * 0 -> the total number of service conversations by day
 * 1 -> the number of nondeflected service conversations by day
 * @param response
 * @returns
 */
export const parseServiceDeflection: ParserFunction<
  Report<[string]>,
  LineGraphResponse
> = (response) => {
  return map((row) => {
    const serviceConversations = row.metrics[0]
    const nondeflectedConversations = row.metrics[1]
    const deflectedConversations =
      serviceConversations - nondeflectedConversations
    const deflectionRate = serviceConversations
      ? deflectedConversations / serviceConversations
      : 0
    return [row.dimensions[0], ensureNoNan(deflectionRate * 100)]
  }, response.data.rows)
}

interface IDeflectionRateByPlaybook {
  playbookId: string
  deflectionRate: string
}

/**
 * Row Definitions:
 * 0 -> the total number of service conversations by day
 * 1 -> the number of nondeflected service conversations by day
 * @param response
 * @returns
 */
export const parseDeflectionRateByPlaybook = (
  response: Report<[string]>
): IDeflectionRateByPlaybook[] => {
  return map((row) => {
    const serviceConversations = row.metrics[0]
    const nondeflectedConversations = row.metrics[1]
    const deflectedConversations =
      serviceConversations - nondeflectedConversations
    const deflectionRate = serviceConversations
      ? deflectedConversations / serviceConversations
      : 0
    return {
      playbookId: row.dimensions[0],
      deflectionRate: `${ensureNoNan(formatNumber(deflectionRate * 100))}%`,
    }
  }, response.data.rows)
}

type IPieChartData = [string, number]

/**
 * Row Definitions:
 * 0 -> the total number of service conversations by day
 * 1 -> the number of nondeflected service conversations by day
 * @param response
 * @returns
 */
export const parseSupportConversations = (
  response: Report<[string]>
): IPieChartData[] => {
  const result = map((row) => {
    return [
      ['Deflected', row.metrics[0] - row.metrics[1]],
      ['Not Deflected', row.metrics[1]],
    ]
  }, response.data.rows)
  return result[0] as IPieChartData[]
}

export const parseOverallCSATRating = (response: Report<[string]>): string => {
  return formatNumber(response.data.rows[0].metrics[1], 1)
}

export const parseCSATByRating = (
  response: Report<[string]>
): IPieChartData[] => {
  const total = map((row) => {
    return row.metrics[0]
  }, response.data.rows).reduce((partialSum, a) => partialSum + a, 0)

  const result = map((row) => {
    const percentage = formatNumber((row.metrics[0] / total) * 100, 1)
    return [`${row.dimensions[0]}/5 (${percentage}%)`, row.metrics[0]]
  }, response.data.rows).reverse()
  return result as IPieChartData[]
}

export interface ICSATByTeammate {
  user: string
  convoCount: string
  csat: string
  lastRated: string
}

const getDate = (date) => {
  const momentDate = moment(date)
  return momentDate.isValid() ? momentDate.format('MM/DD/YY') : '-'
}

export const parseCSATByTeammate = (
  response: Report<[string]>
): ICSATByTeammate[] => {
  return map((row) => {
    const date = row.metrics[2]

    return {
      user: row.dimensions[0],
      convoCount: formatNumber(row.metrics[0]),
      csat: formatNumber(row.metrics[1], 1),
      lastRated: date ? getDate(date) : '',
    }
  }, response.data.rows)
}
